/**
 * Power-Up Manager
 * Handles power-up spawning and collection
 */

import { THREE } from "../config/ThreeConfig.js";

function toVector3(position) {
  if (!position) return null;
  if (position instanceof THREE.Vector3) return position;
  if (typeof position.x === "number" && typeof position.y === "number" && typeof position.z === "number") {
    return new THREE.Vector3(position.x, position.y, position.z);
  }
  return null;
}

export class PowerUpManager {
  constructor(game) {
    this.game = game;
    this.powerUps = [];
  }
  
  async init() {
    return Promise.resolve();
  }
  
  spawnPowerUp(position, type = null) {
    if (!type) {
      const types = ['laser', 'shield', 'bomb', 'health', 'life', 'boost'];
      type = types[Math.floor(Math.random() * types.length)];
    }
    
    const spawnPosition = toVector3(position) || new THREE.Vector3(0, 0, -100);
    const powerUp = new PowerUp(this.game, type, spawnPosition);
    this.powerUps.push(powerUp);
    this.game.scene.add(powerUp.object3D);
    
    return powerUp;
  }
  
  collectPowerUp(powerUp, player) {
    powerUp.collect(player);
    // Remove from scene and array
    this.game.scene.remove(powerUp.object3D);
    const index = this.powerUps.indexOf(powerUp);
    if (index > -1) {
      this.powerUps.splice(index, 1);
    }
  }
  
  update(deltaTime, _player) {
    for (let i = this.powerUps.length - 1; i >= 0; i--) {
      const powerUp = this.powerUps[i];
      powerUp.update(deltaTime);
      
      // Remove if expired
      if (powerUp.isExpired()) {
        this.game.scene.remove(powerUp.object3D);
        this.powerUps.splice(i, 1);
      }
    }
  }
  
  clearAll() {
    for (const powerUp of this.powerUps) {
      this.game.scene.remove(powerUp.object3D);
    }
    this.powerUps.length = 0;
  }
  
  dispose() {
    this.clearAll();
  }
}

/**
 * Power-Up Class
 */
class PowerUp {
  constructor(game, type, position) {
    this.game = game;
    this.type = type;
    this.position = position ? position.clone() : new THREE.Vector3(0, 0, -100);
    this.age = 0;
    this.maxAge = 30; // 30 seconds
    
    this.object3D = new THREE.Group();
    this.mesh = null;
    
    this.createMesh();
  }
  
  createMesh() {
    const colors = {
      laser: 0xff0000,
      shield: 0x00ff00,
      bomb: 0xff6600,
      health: 0xff00ff,
      life: 0x00ffff,
      boost: 0xffff00,
    };
    
    const geometry = new THREE.OctahedronGeometry(3, 0);
    const material = new THREE.MeshPhongMaterial({
      color: colors[this.type] || 0xffffff,
      emissive: colors[this.type] || 0xffffff,
      emissiveIntensity: 0.65,
      shininess: 120,
    });
    
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.castShadow = true;
    this.object3D.add(this.mesh);

    // A small additive core improves pickup readability against vibrant nebula (no textures needed).
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(1.6, 10, 10),
      new THREE.MeshBasicMaterial({
        color: colors[this.type] || 0xffffff,
        transparent: true,
        opacity: 0.28,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
        toneMapped: false,
      })
    );
    core.name = "powerup-core";
    core.renderOrder = 10;
    this.object3D.add(core);
    
    // Add floating animation offset
    this.floatOffset = Math.random() * Math.PI * 2;
    
    this.object3D.position.copy(this.position);
  }
  
  update(deltaTime) {
    this.age += deltaTime;
    
    // Rotate
    this.object3D.rotation.y += deltaTime * 2;
    this.object3D.rotation.z += deltaTime;
    
    // Float
    this.object3D.position.y = this.position.y + Math.sin(this.age * 2 + this.floatOffset) * 2;
  }
  
  collect(player) {
    player.powerUp(this.type);
    
    // Visual feedback
    if (this.game.feedback && this.game.feedback.playPowerUpEffect) {
      this.game.feedback.playPowerUpEffect(this.type, this.object3D.position.clone());
    }
    
    this.game.uiManager.showMessage(`${this.type.toUpperCase()} POWER-UP!`, 1000);
    this.game.soundManager.playSFX('powerup');
  }
  
  isExpired() {
    return this.age >= this.maxAge;
  }
  
  getBoundingBox() {
    return new THREE.Box3().setFromObject(this.object3D);
  }
}
